import React, { useEffect, useState } from "react"
import {
    Autocomplete,
    TextField,
    createFilterOptions,
    Box,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
    Tooltip,
} from "@mui/material"
import { useCreateTagToTicketMutation, useMapTagToTicketMutation } from "../../pages/Common/commonService"
import { useDispatch, useSelector } from "react-redux"
import { TagIcon } from "./TagIcon"
import { setTableFetchValue } from "../../pages/Finance/FinanceSlice"
import { GetTagRequest } from "../../pages/Common/commonTypes"
import { COLOR_OPTIONS } from "../../enums/dropdownValues"

type CreatableOption = { inputValue: string; name: string }
type Option = GetTagRequest | CreatableOption

type Props = {
    tags: GetTagRequest[]
    allTagsOptions: GetTagRequest[]
    setTags: any
    ticket_id: number
    module_name: string
    ticket_type: string
    view?: string
    addAccess?: boolean
    mapAccess?: boolean
}

const filter = createFilterOptions<Option>()

function CreatableTagsInput({
    tags,
    setTags,
    allTagsOptions,
    ticket_id,
    module_name,
    ticket_type,
    view,
    addAccess,
    mapAccess,
}: Props) {
    const [inputValue, setInputValue] = useState("")
    const [selectedTags, setSelectedTags] = useState<Option[]>([])
    const [selectedColor, setSelectedColor] = useState<string>(COLOR_OPTIONS[0]?.color_code)

    const [mapTagToTicket] = useMapTagToTicketMutation()
    const [createTagToTicket] = useCreateTagToTicketMutation()
    const { user_id } = useSelector((store: any) => store.verifyLogin)
    const dispatch = useDispatch()
    const enterHandledRef = React.useRef(false)

    useEffect(() => {
        setSelectedTags(tags)
    }, [tags])

    const uniqueTagOptions = React.useMemo(() => {
        const seenNames = new Set<string>()
        return allTagsOptions?.filter(tag => {
            const name = tag.name.toLowerCase().trim()
            if (seenNames.has(name)) return false
            seenNames.add(name)
            return true
        })
    }, [allTagsOptions])

    const filteredTagOptions = uniqueTagOptions

    const handleChange = async (event: React.SyntheticEvent, newValue: (string | Option)[]) => {
        if (enterHandledRef.current) {
            enterHandledRef.current = false
            return
        }

        const resolvedTags: GetTagRequest[] = []
        let currentTagsCount = tags.length
        dispatch(setTableFetchValue(false))
        for (const item of newValue) {
            if (typeof item !== "string" && "inputValue" in item) {
                if (!addAccess) continue

                const tagName = item.inputValue.trim()
                const isPrimaryTag = currentTagsCount === 0

                try {
                    const createPayload: any = {
                        tag_id: 0,
                        ticket_id,
                        name: tagName,
                        color_code: selectedColor,
                        text_color_code: selectedColor,
                        tag_name: tagName,
                        is_primary: isPrimaryTag ? 1 : 0,
                        module: module_name,
                        view: view && (ticket_type === "Projects" || ticket_type === "Demolition") ? view : "detail",
                        user_id,
                    }
                    createPayload.ticket_type = ticket_type

                    await createTagToTicket(createPayload).unwrap()

                    const realTag = allTagsOptions.find(t => t.name.toLowerCase() === tagName.toLowerCase())
                    if (realTag) {
                        if (isPrimaryTag) realTag.is_primary = 1
                        resolvedTags.push(realTag)
                        currentTagsCount += 1
                    } else {
                        console.warn("New tag not found in tag list")
                    }
                } catch (error) {
                    console.error("Tag creation failed:", error)
                }
            } else if (typeof item !== "string") {
                if (!mapAccess) continue

                const alreadyMapped = tags.some(tag => tag.id === item.id)
                if (!alreadyMapped) {
                    const totalTagsCount = currentTagsCount + 1
                    const isPrimaryTag = totalTagsCount === 1
                    const payload: any = {
                        tag_id: item.id,
                        ticket_id,
                        tag_name: item.name,
                        is_primary: isPrimaryTag ? 1 : 0,
                        color_code: item.color_code,
                        text_color_code: item.text_color_code,
                        module: module_name,
                        view: view && (ticket_type === "Projects" || ticket_type === "Demolition") ? view : "detail",
                        user_id,
                    }

                    payload.ticket_type = ticket_type

                    try {
                        await mapTagToTicket(payload).unwrap()
                        if (isPrimaryTag) item.is_primary = 1
                        resolvedTags.push(item)
                        currentTagsCount += 1
                    } catch (error) {
                        console.error("Tag mapping failed:", error)
                    }
                }
            }
        }

        dispatch(setTableFetchValue(true))
        const updatedTags = [...tags, ...resolvedTags.filter(tag => !tags.some(t => t.id === tag.id))]
        setSelectedTags(updatedTags)
        setTags(updatedTags)
    }

    const handleKeyDown = async (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== "Enter") return

        const inputEl = event.target as HTMLInputElement
        const tagName = inputEl.value?.trim()

        if (!tagName) return

        enterHandledRef.current = true

        const inputLower = tagName.toLowerCase()
        const matchedExistingTag = allTagsOptions.find(tag => tag.name.toLowerCase() === inputLower)
        const alreadyMapped = tags.some(tag => tag.name.toLowerCase() === inputLower)
        const isFirstTag = tags.length === 0

        dispatch(setTableFetchValue(false))

        if (matchedExistingTag && !alreadyMapped && mapAccess) {
            try {
                await mapTagToTicket({
                    tag_id: matchedExistingTag.id,
                    ticket_id,
                    tag_name: matchedExistingTag.name,
                    is_primary: isFirstTag ? 1 : 0,
                    color_code: matchedExistingTag.color_code,
                    text_color_code: matchedExistingTag.text_color_code,
                    module: module_name,
                    view: view && ticket_type === "Projects" ? view : "detail",
                    user_id,
                    ticket_type,
                }).unwrap()

                if (isFirstTag) matchedExistingTag.is_primary = 1

                const updatedTags = [...tags, matchedExistingTag]
                setTags(updatedTags)
                setSelectedTags(updatedTags)
                setInputValue("")
            } catch (error) {
                console.error("Tag mapping failed:", error)
            }
        } else if (!matchedExistingTag && addAccess) {
            try {
                await createTagToTicket({
                    tag_id: 0,
                    ticket_id,
                    name: tagName,
                    color_code: selectedColor,
                    tag_name: tagName,
                    ticket_type,
                    text_color_code: "#ffffff",
                    is_primary: isFirstTag ? 1 : 0,
                    module: module_name,
                    view: view && (ticket_type === "Projects" || ticket_type === "Demolition") ? view : "detail",
                    user_id,
                }).unwrap()

                const newTag = allTagsOptions.find(t => t.name.toLowerCase() === inputLower)
                if (newTag && mapAccess) {
                    await mapTagToTicket({
                        tag_id: newTag.id,
                        ticket_id,
                        tag_name: newTag.name,
                        is_primary: isFirstTag ? 1 : 0,
                        color_code: newTag.color_code,
                        text_color_code: newTag.text_color_code,
                        module: module_name,
                        view: view && ticket_type === "Projects" ? view : "detail",
                        user_id,
                        ticket_type,
                    }).unwrap()

                    if (isFirstTag) newTag.is_primary = 1

                    const updatedTags = [...tags, newTag]
                    setTags(updatedTags)
                    setSelectedTags(updatedTags)
                    setInputValue("")
                }
            } catch (error) {
                console.error("Failed to create/map new tag:", error)
            }
        }

        dispatch(setTableFetchValue(true))
    }

    const handleColorChange = (event: SelectChangeEvent) => {
        setSelectedColor(event.target.value)
    }

    return (
        <Box display="flex" alignItems="center" gap={1}>
            <Select
                size="small"
                value={selectedColor}
                onChange={handleColorChange}
                displayEmpty
                sx={{
                    width: 40,
                    height: 25.25,
                    ".MuiSelect-select": {
                        paddingRight: "0 !important",
                        paddingLeft: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        height: "100%",
                        boxSizing: "border-box",
                        borderRadius: 0,
                        backgroundColor: "rgba(232, 232, 236, 0.2)",
                    },
                    "& input": {
                        backgroundColor: "rgba(232, 232, 236, 0.2) !important",
                    },
                    ".MuiSvgIcon-root": {
                        display: "none",
                    },
                    "& fieldset": {
                        border: "none",
                        backgroundColor: "rgba(232, 232, 236, 0.2) !important",
                        borderBottom: "1px solid #E5E7EB !important",
                    },
                }}
                renderValue={value => {
                    if (!value) return <Box sx={{ width: 15, height: 15 }} />
                    return (
                        <Box
                            sx={{
                                width: 15,
                                height: 15,
                                borderRadius: "50%",
                                bgcolor: value,
                                margin: "auto",
                            }}
                        />
                    )
                }}>
                {COLOR_OPTIONS.map(({ color_code }) => (
                    <MenuItem key={color_code} value={color_code} sx={{ display: "flex", justifyContent: "center" }}>
                        <Box
                            sx={{
                                width: 17,
                                height: 17,
                                borderRadius: "50%",
                                bgcolor: color_code,
                                margin: "auto",
                            }}
                        />
                    </MenuItem>
                ))}
            </Select>

            <Autocomplete<Option, true, true, boolean>
                multiple
                selectOnFocus
                clearOnBlur
                disableClearable
                handleHomeEndKeys
                freeSolo={addAccess}
                options={filteredTagOptions}
                value={selectedTags}
                inputValue={inputValue}
                onKeyDown={handleKeyDown}
                ListboxProps={{
                    style: {
                        maxHeight: 190,
                        overflow: "auto",
                        maxWidth: 200,
                    },
                }}
                onInputChange={(e, newInputValue) => setInputValue(newInputValue)}
                onChange={handleChange}
                filterOptions={(options, params) => {
                    const filtered = filter(options, params)
                    const inputLower = params.inputValue.toLowerCase().trim()
                    const isExisting = allTagsOptions.some(tag => tag.name.toLowerCase() === inputLower)

                    if (params.inputValue !== "" && !isExisting && addAccess) {
                        filtered.push({
                            inputValue: params.inputValue,
                            name: `${params.inputValue}(New Tag)`,
                        })
                    }

                    return filtered
                }}
                getOptionLabel={option =>
                    typeof option === "string" ? option : "inputValue" in option ? option.name : option.name
                }
                renderTags={() => null}
                renderOption={(props, option) => {
                    const isAlreadySelected = "name" in option && tags.some(tag => tag.name === option.name)
                    const isNewTag = "inputValue" in option
                    return (
                        <li
                            {...props}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                opacity: isAlreadySelected ? 0.5 : 1,
                                pointerEvents: isAlreadySelected ? "none" : "auto",
                                cursor: isAlreadySelected ? "not-allowed" : "pointer",
                            }}>
                            {isNewTag ? (
                                <>
                                    <TagIcon />
                                    <Typography>
                                        <span>{option.inputValue}</span>{" "}
                                        <span style={{ color: "#B0B0B0" }}>(New Tag)</span>
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <TagIcon style={{ color: option.color_code }} />
                                    {option.name.length > 18 ? (
                                        <Tooltip title={option.name}>
                                            <Typography
                                                sx={{
                                                    color: option.color_code,
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    maxWidth: 100,
                                                }}>
                                                {option.name}
                                            </Typography>
                                        </Tooltip>
                                    ) : (
                                        <Typography sx={{ color: option.color_code }}>{option.name}</Typography>
                                    )}
                                </>
                            )}
                        </li>
                    )
                }}
                renderInput={params => (
                    <TextField
                        {...params}
                        placeholder="Add a tag..."
                        variant="standard"
                        InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                            startAdornment: (
                                <Box sx={{ display: "flex", alignItems: "center", pl: 1 }}>
                                    {params.InputProps.startAdornment}
                                </Box>
                            ),
                        }}
                        sx={{
                            flexGrow: 1,
                            backgroundColor: "#FAFAFB",
                            border: "none !important",
                            borderRadius: 1,
                            minWidth: 200,
                            fontSize: 14,
                            color: "#555",
                            "& input": {
                                padding: "6px 0",
                                fontSize: "12px",
                            },
                            "& .MuiInputBase-root": {
                                padding: 0,
                            },
                        }}
                    />
                )}
            />
        </Box>
    )
}

export default CreatableTagsInput
